import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getVibeCategories,
  createVibeCategory,
  getMyFavorites,
} from "../../api/vibe";
import { toast } from "react-toastify";
import { createVibe } from "../../api/categories"; // ✅ import createVibe
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

const Step1SelectVibe = ({ formData, setFormData, goNext, goBack }) => {
  const { t } = useTranslation();
  const [vibes, setVibes] = useState([]);
  const [loadingVibes, setLoadingVibes] = useState(true);
  const [isVibeModalOpen, setIsVibeModalOpen] = useState(false);
  const [newVibeName, setNewVibeName] = useState("");
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [saving, setSaving] = useState(false); // ✅ for button state

  const location = useLocation();

  useEffect(() => {
    async function fetchFavorites() {
      const res = await getMyFavorites();
      setFavoriteCount(res.data.total || 0);
    }
    fetchFavorites();
  }, []);

  useEffect(() => {
    const fetchVibeCategories = async () => {
      try {
        setLoadingVibes(true);
        const res = await getVibeCategories();
        setVibes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setVibes([]);
      } finally {
        setLoadingVibes(false);
      }
    };
    fetchVibeCategories();
  }, []);

  const handleAddVibe = async () => {
    if (newVibeName.trim() === "") {
      toast.error(t("please_enter_vibe_name"));
      return;
    }
    try {
      const res = await createVibeCategory(newVibeName.trim());
      if (res && res.data) {
        setVibes((prev) => [...prev, res.data]);
      }
      setNewVibeName("");
      setIsVibeModalOpen(false);
    } catch (err) {
      toast.error(t("failed_to_create_new_vibe_category"));
    }
  };

  // Save handler -> call createVibe
  const handleSave = async () => {
    if (!formData.vibe_category_id) {
      toast.error(t("please_select_category"));
      return;
    }

    try {
      setSaving(true);
      const payload = {
        vibe_category_id: formData.vibe_category_id,
        steps: 1,
      };

      const res = await createVibe(payload);

      if (res && res.data && res.data.id) {
        setFormData((prev) => ({
          ...prev,
          id: res.data.id,
        }));

        setFormData((prev) => ({
          ...prev,
          members: [],
          vibe_links: [],
          individual_members: [],
        }));
        location.state = {}; // clear state to avoid confusion

        goNext(); // only go next if success
      }
    } catch (err) {
      toast.error(
        t("failed_to_save_vibe", "Failed to save vibe. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  };

  const navigate = useNavigate();

  return (
    <>
      <div className="flex justify-between items-center mb-4">

        <div className="flex items-center gap-2">
          <button
            onClick={() => goBack()}
            className="py-2 rounded-lg text-gray-700 dark:text-white"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h1 className="text-md sm:text-md md:text-lg ml-3 font-bold text-black dark:text-white">
            {t("create_your_vibe", "Create Your Vibe!")}
          </h1>
        </div>
        <button
          className={`text-sm px-4 py-2 rounded-lg font-semibold bg-[#6d29f6] text-white whitespace-nowrap min-w-[120px]`}
          // style={{ color: "#6d29f6" }}
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? <Loader loaderColor="white" size={20} /> : t("save_next", "Save & Next")}
        </button>
      </div>


      {/* Buttons */}
      <div className="flex gap-4 mb-6 flex-1 flex-col">
        <button
          className="w-full text-white py-2 rounded-lg font-semibold relative"
          style={{ background: "#6d29f6" }}
        >
          <Link to="/favorite">{t("favorites", "Favorites")}</Link>
          <span
            className="text-xs absolute right-[-10px] top-[-10px] bg-[#6d29f6] text-white text-sm w-6 h-6 flex items-center justify-center rounded-full border-2 border-white shadow"
            style={{ fontWeight: "bold" }}
          >
            {favoriteCount}
          </span>
        </button>

        <button
          className="flex-1 text-white py-2 rounded-lg font-semibold"
          style={{ background: "#6d29f6" }}
        >
          <Link to="/create-poll">{t("poll", "Poll")}</Link>
        </button>
      </div>

      {/* Vibe Grid */}
      <div className="grid grid-cols-2 gap-4 mb-10 p-4 pt-4">

        {loadingVibes ? (
          <div className="col-span-2 text-center text-gray-500">
            {t("loading_categories", "Loading categories...")}
          </div>
        ) : vibes.length === 0 ? (
          <div className="col-span-2 text-center text-gray-500">
            {t("no_categories_found", "No categories found.")}
          </div>
        ) : (
          vibes.map((vibe) => (
            <div
              key={vibe.id}
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  vibeName: vibe.name,
                  vibe_category_id: vibe.id,
                }))
              }
              className={`text-xs sm:text-md md:text-lg relative p-2 rounded-lg border-2 text-center cursor-pointer font-semibold ${formData.vibe_category_id === vibe.id
                  ? "bg-[#6d29f6] text-white border-[#6d29f6]"
                  : "bg-white dark:bg-white border-[#6D29F6]"
                }`}
              style={
                formData.vibe_category_id !== vibe.id
                  ? { color: "#000000" }
                  : undefined
              }
            >
              {vibe.name}
              {/* <span
                className="absolute top-[-7px] right-[-10px] text-white text-xs rounded-full px-2 py-1 font-bold border-2 border-white"
                style={{ background: "#6d29f6" }}
              >
                  
              </span> */}
              <span
                className="absolute text-xs right-[-10px] top-[-10px] bg-[#6d29f6] text-white w-6 h-6 flex items-center justify-center rounded-full border-2 border-white shadow"
                style={{ fontWeight: "bold" }}
              >
                {vibe.count}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Create New Vibe */}

      <button
        onClick={() => setIsVibeModalOpen(true)}
        className="w-full text-white py-2 rounded-lg font-semibold shadow-lg"
        style={{ background: "#6d29f6" }}
      >
        {t("create_new_vibe", "Create New Vibe")}
      </button>







      {/* Modal */}
      {isVibeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
         <div className="bg-white dark:bg-dark-card rounded-2xl shadow-xl w-full max-w-xs p-6 flex flex-col items-center border border-white dark:border-dark-border">
 <label className="text-lg font-semibold mb-3 text-gray-500 dark:text-dark-text-secondary w-full text-left">
              {t("vibe_name", "Vibe Name")}
            </label>
            <input
              type="text"
              value={newVibeName}
              onChange={(e) => setNewVibeName(e.target.value)}
              className="
    w-full p-3 
    bg-gray-100 dark:bg-[#1f2130] 
    text-gray-700 dark:text-dark-text 
    text-base font-medium
    rounded-lg mb-6
    
    border border-gray-300 dark:border-gray-600
    focus:outline-none
    focus:ring-2 focus:ring-[#6d29f6]
    dark:focus:ring-[#6d29f6]
  "
              placeholder={t('enter_vibe_name', 'Enter Vibe Name')}
            />

            <div className="flex gap-4 w-full">
              <button
                onClick={() => setIsVibeModalOpen(false)}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg font-bold text-lg"
              >
                {t("cancel", "Cancel")}
              </button>
              <button
                onClick={handleAddVibe}
                className="flex-1 py-2 bg-[#6d29f6] text-white rounded-lg font-bold text-lg"
              >
                {t("save", "Save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Step1SelectVibe;
